import { CUSTOM_ELEMENTS_SCHEMA, NgModule, APP_INITIALIZER } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { OAuthModule, OAuthStorage, OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';

import { NdbxIconModule } from '@allianz/ngx-ndbx/icon';
import { NxAccordionModule } from '@aposin/ng-aquila/accordion';
import { NxActionModule } from '@aposin/ng-aquila/action';
import { NxBadgeModule } from '@aposin/ng-aquila/badge';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxCheckboxModule } from '@aposin/ng-aquila/checkbox';
import { NxCopytextModule } from '@aposin/ng-aquila/copytext';
import { NX_DATE_LOCALE, NxDatefieldModule } from '@aposin/ng-aquila/datefield';
import { NxIsoDateModule } from '@aposin/ng-aquila/iso-date-adapter';
import { NxDropdownModule } from '@aposin/ng-aquila/dropdown';
import { NxNativeDateModule } from '@aposin/ng-aquila/datefield';
import { NxErrorModule } from '@aposin/ng-aquila/base';
import { NxFileUploaderModule } from '@aposin/ng-aquila/file-uploader';
import { NxFooterModule } from '@aposin/ng-aquila/footer';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxGridModule } from '@aposin/ng-aquila/grid';
import { NxHeaderModule } from '@aposin/ng-aquila/header';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxImageModule } from '@aposin/ng-aquila/image';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxLinkModule } from '@aposin/ng-aquila/link';
import { NxMenuModule } from '@aposin/ng-aquila/menu';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NxPaginationModule } from '@aposin/ng-aquila/pagination';
import { NxSidebarModule } from '@aposin/ng-aquila/sidebar';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxTableModule } from '@aposin/ng-aquila/table';
import { NxTabsModule } from '@aposin/ng-aquila/tabs';
import { NxTreeModule } from '@aposin/ng-aquila/tree';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NxContextMenuModule } from '@aposin/ng-aquila/context-menu';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { DashboardComponent } from './dashboard/dashboard.component';
import { MetadataFormComponent } from './document/metadata-form/metadata-form.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component';
import { UploadFormComponent } from './upload-form/upload-form.component';
import { NxTooltipModule } from '@aposin/ng-aquila/tooltip';
import { SessionTimerComponent } from './interceptors/sessions.timer.component';
import { NxProgressbarModule } from '@aposin/ng-aquila/progressbar';

import localeDe from '@angular/common/locales/de';

import 'dayjs/locale/de.js';
import { registerLocaleData } from '@angular/common';
import { ApiModule } from '../api/api.module';
import { ListComponent } from './document/list/list.component';
import { DetailsComponent } from './document/details/details.component';
import { SearchComponent } from './document/search/search.component';
import { TabsComponent } from './tabs/tabs.component';
import { MenuComponent } from './menu/menu.component';
import { NxModalModule } from '@aposin/ng-aquila/modal';
import { InterceptorsModule } from './interceptors/interceptors.module';
import { authCodeFlowConfig, logAuthTokenLifecycle } from './auth.config';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export function createTranslateLoader(http: HttpClient): TranslateLoader {
	return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeAuth(oAuthService: OAuthService, router: Router){
  return async (): Promise<void> => {
	console.log("AppModule initializeAuth started");
    oAuthService.configure(authCodeFlowConfig);
	console.log("AppModule loadDiscoveryDocumentAndTryLogin started");
    oAuthService.loadDiscoveryDocumentAndTryLogin().
		then(() => {
			console.log('AppModule loadDiscoveryDocumentAndTryLogin completed')						
			try{
				if (oAuthService.hasValidAccessToken()){
					oAuthService.setupAutomaticSilentRefresh();
				}
			}catch(e){
				console.error(`AppModuleEvent setupAutomaticSilentRefresh failed ${e}`);
			}			
		}).
		catch(err => console.error('AppModule loadDiscoveryDocumentAndTryLogin failed',err));
	console.log("AppModule loadDiscoveryDocumentAndTryLogin completed");
	// oAuthService.setupAutomaticSilentRefresh();
	oAuthService.events.pipe()		
				.subscribe(event => {
					if (event instanceof OAuthErrorEvent) {
					  console.info(`AppModuleEvent event ${JSON.stringify(event)}`);
					} else {
					  console.info(`AppModuleEvent event ${JSON.stringify(event)}`);
					}
				  });
	oAuthService.events
				.pipe(filter((e) => e.type === 'token_received'))
				.subscribe((event) => {
					console.info(`AppModuleEvent ${JSON.stringify(event)}`);
					logAuthTokenLifecycle(oAuthService);
					try{
						oAuthService.loadUserProfile().then(e => {
							console.info(`AppModuleEvent loadUserProfile completed ${JSON.stringify(e)}`);
							try{
								oAuthService.setupAutomaticSilentRefresh();
							}catch(e){
								console.error(`AppModuleEvent setupAutomaticSilentRefresh failed ${e}`);
							}
						}).catch(e => {
							console.error(`AppModuleEvent loadUserProfile failed ${e}`);});
					}catch(e){
						console.error(`AppModuleEvent loadUserProfile failed ${e}`);
					}
								
					// window.location.href = '/dashboard';
					console.info(`AppModuleEvent before navigate to dashboard ${window.location.pathname}`);
					if (!window.location.pathname.startsWith('/dashboard')){
						console.info(`AppModuleEvent before navigate to dashboard}`);		
						window.location.href = '/dashboard';
						console.info(`AppModuleEvent after navigate to dashboard}`);	
					}					
				});
  }
}

registerLocaleData(localeDe);

@NgModule({
	declarations: [
		AppComponent,
		DashboardComponent,
		MetadataFormComponent,
		HomeComponent,
		LoginComponent,
		UploadFormComponent,
		ListComponent,
		DetailsComponent,
		SearchComponent,
		TabsComponent,
		MenuComponent,
		SessionTimerComponent
	],
	imports: [
		AppRoutingModule,
		BrowserAnimationsModule,
		BrowserModule,
		FormsModule,
		HttpClientModule,
		NdbxIconModule,
		NxAccordionModule,
		NxActionModule,
		NxBadgeModule,
		NxButtonModule,
		NxCheckboxModule,
		NxCopytextModule,
		NxDropdownModule,
		NxDatefieldModule,
		NxNativeDateModule,
		NxModalModule,
		NxProgressbarModule,
		NxErrorModule,
		NxFileUploaderModule,
		NxFooterModule,
		NxFormfieldModule,
		NxGridModule,
		NxHeaderModule,
		NxHeadlineModule,
		InterceptorsModule,
		NxIconModule,
		NxImageModule,
		NxInputModule,
		NxLinkModule,
		NxMenuModule,
		NxMessageModule,
		NxIsoDateModule,
		NxPaginationModule,
		NxSidebarModule,
		NxSpinnerModule,
		NxTableModule,
		NxTabsModule,
		NxTreeModule,
		NxRadioModule,
		NxTooltipModule,
		NxRadioToggleModule,
		OAuthModule.forRoot(),
		ReactiveFormsModule,
		TranslateModule.forRoot({
			defaultLanguage: 'de',
			loader: {
				provide: TranslateLoader,
				useFactory: (createTranslateLoader),
				deps: [HttpClient],
			},
		}),
		NxContextMenuModule,
		ApiModule,
	],
	providers: [
		{ provide: OAuthStorage, useValue: localStorage },
		{ provide: NX_DATE_LOCALE, useValue: 'de' },
		{
			provide: APP_INITIALIZER,
			useFactory: initializeAuth,
			deps: [OAuthService],
			multi: true,
			},
		DatePipe
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {

}
